import { useState, useMemo } from "react";
import { Plus, Trash2, X, RotateCcw, Clock, MapPin, User, CheckCircle2, AlertTriangle, Edit3, Save, ChevronDown, Download } from "lucide-react";
import { exportSchedulePdf } from "@/lib/pdfExport";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type DbOfficer = Database["public"]["Tables"]["officers"]["Row"];
type DbSite = Database["public"]["Tables"]["sites"]["Row"];
type DbCheckpoint = Database["public"]["Tables"]["checkpoints"]["Row"];
type DbSchedule = Database["public"]["Tables"]["schedules"]["Row"];
type DbCheckIn = Database["public"]["Tables"]["check_ins"]["Row"];

interface PatrolSchedulerProps {
  officers: DbOfficer[];
  sites: DbSite[];
  checkpoints: DbCheckpoint[];
  schedules: DbSchedule[];
  checkIns: DbCheckIn[];
  addSchedule: any;
  updateSchedule: any;
  removeSchedule: any;
  generateRotation: any;
}

export default function PatrolScheduler({
  officers, sites, checkpoints, schedules, checkIns,
  addSchedule, updateSchedule, removeSchedule, generateRotation,
}: PatrolSchedulerProps) {
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [viewDate, setViewDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ officerId: "", checkpointId: "", dueTime: "" });

  // Generator form
  const [genForm, setGenForm] = useState({
    siteId: "",
    shiftStart: "06:00",
    shiftEnd: "18:00",
    interval: 60,
    selectedOfficers: [] as string[],
    selectedCheckpoints: [] as string[],
  });

  // Manual add form
  const [manualForm, setManualForm] = useState({ siteId: "", officerId: "", checkpointId: "", dueTime: "" });
  const [scheduleFilter, setScheduleFilter] = useState<"all" | "overdue" | "completed" | "pending">("all");

  const siteCheckpoints = useMemo(() =>
    selectedSite ? checkpoints.filter(cp => cp.site_id === selectedSite) : checkpoints,
    [selectedSite, checkpoints]
  );

  const siteOfficers = useMemo(() =>
    selectedSite ? officers.filter(o => o.site_id === selectedSite || !o.site_id) : officers,
    [selectedSite, officers]
  );

  // Generator-specific filtered lists (based on genForm.siteId)
  const genSiteCheckpoints = useMemo(() =>
    genForm.siteId ? checkpoints.filter(cp => cp.site_id === genForm.siteId) : [],
    [genForm.siteId, checkpoints]
  );

  const genSiteOfficers = useMemo(() =>
    genForm.siteId ? officers.filter(o => o.site_id === genForm.siteId || !o.site_id) : officers,
    [genForm.siteId, officers]
  );

  // Build enriched schedule rows for the selected date
  const enrichedSchedule = useMemo(() => {
    const dayStart = new Date(`${viewDate}T00:00:00`);
    const dayEnd = new Date(`${viewDate}T23:59:59`);

    return schedules
      .filter(s => {
        const d = new Date(s.due_at);
        if (d < dayStart || d > dayEnd) return false;
        if (selectedSite && s.site_id !== selectedSite) return false;
        return true;
      })
      .map(s => {
        const officer = officers.find(o => o.id === s.officer_id);
        const checkpoint = checkpoints.find(cp => cp.id === s.checkpoint_id);
        const site = sites.find(st => st.id === s.site_id);
        const dueDate = new Date(s.due_at);

        // Find matching check-in (same officer + checkpoint + within 30 min of due time)
        const matchingCheckIn = checkIns.find(ci => {
          if (ci.officer_id !== s.officer_id) return false;
          if (s.checkpoint_id && ci.checkpoint_id !== s.checkpoint_id) return false;
          const ciTime = new Date(ci.checked_at);
          const diff = Math.abs(ciTime.getTime() - dueDate.getTime());
          return diff < 30 * 60 * 1000; // within 30 min window
        });

        const now = new Date();
        const isOverdue = !matchingCheckIn && s.status !== "completed" && dueDate < now;
        const overdueMinutes = isOverdue ? Math.round((now.getTime() - dueDate.getTime()) / 60000) : 0;
        const isDueSoon = !matchingCheckIn && !isOverdue && dueDate > now && (dueDate.getTime() - now.getTime()) < 15 * 60 * 1000;

        let varianceMinutes: number | null = null;
        if (matchingCheckIn) {
          varianceMinutes = Math.round((new Date(matchingCheckIn.checked_at).getTime() - dueDate.getTime()) / 60000);
        }

        let status: "completed" | "overdue" | "due_soon" | "pending" = "pending";
        if (matchingCheckIn || s.status === "completed") status = "completed";
        else if (isOverdue) status = "overdue";
        else if (isDueSoon) status = "due_soon";

        return {
          id: s.id,
          dueAt: dueDate,
          dueTime: dueDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          officerName: officer?.name || "Unassigned",
          officerId: s.officer_id || "",
          checkpointName: checkpoint?.name || "—",
          checkpointId: s.checkpoint_id || "",
          siteName: site?.name || "—",
          siteId: s.site_id || "",
          status,
          isOverdue,
          overdueMinutes,
          isDueSoon,
          actualTime: matchingCheckIn ? new Date(matchingCheckIn.checked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null,
          varianceMinutes,
          method: matchingCheckIn?.method || null,
          gpsCoords: matchingCheckIn?.coords ? matchingCheckIn.coords : null,
          onTime: matchingCheckIn?.on_time ?? null,
        };
      })
      .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
  }, [schedules, checkIns, officers, checkpoints, sites, viewDate, selectedSite]);

  // Stats
  const stats = useMemo(() => {
    const total = enrichedSchedule.length;
    const completed = enrichedSchedule.filter(s => s.status === "completed").length;
    const overdue = enrichedSchedule.filter(s => s.status === "overdue").length;
    const pending = enrichedSchedule.filter(s => s.status === "pending" || s.status === "due_soon").length;
    return { total, completed, overdue, pending, coverage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [enrichedSchedule]);

  const handleGenerateRotation = async () => {
    if (!genForm.siteId) { toast.error("Select a site first"); return; }
    if (genForm.selectedOfficers.length === 0) { toast.error("Select at least one officer"); return; }
    if (genForm.selectedCheckpoints.length === 0) { toast.error("Select at least one checkpoint"); return; }

    try {
      const count = await generateRotation.mutateAsync({
        siteId: genForm.siteId,
        officerIds: genForm.selectedOfficers,
        checkpointIds: genForm.selectedCheckpoints,
        date: viewDate,
        shiftStart: genForm.shiftStart,
        shiftEnd: genForm.shiftEnd,
        intervalMinutes: genForm.interval,
      });
      toast.success(`${count} schedule entries generated with rotation`);
      setSelectedSite(genForm.siteId); // Switch main view to generated site
      setShowGenerator(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to generate schedules");
    }
  };

  // Manual add - computed lists based on manualForm.siteId
  const manualSiteOfficers = useMemo(() =>
    manualForm.siteId ? officers.filter(o => o.site_id === manualForm.siteId || !o.site_id) : officers,
    [manualForm.siteId, officers]
  );
  const manualSiteCheckpoints = useMemo(() =>
    manualForm.siteId ? checkpoints.filter(cp => cp.site_id === manualForm.siteId) : [],
    [manualForm.siteId, checkpoints]
  );

  const handleManualAdd = async () => {
    if (!manualForm.officerId || !manualForm.dueTime) { toast.error("Officer and time are required"); return; }
    if (!manualForm.siteId) { toast.error("Select a site first"); return; }
    const dueAt = new Date(`${viewDate}T${manualForm.dueTime}:00`);
    try {
      await addSchedule.mutateAsync({
        officer_id: manualForm.officerId,
        checkpoint_id: manualForm.checkpointId || null,
        site_id: manualForm.siteId,
        due_at: dueAt.toISOString(),
        status: "pending",
      });
      toast.success("Schedule entry added");
      setSelectedSite(manualForm.siteId);
      setManualForm({ siteId: "", officerId: "", checkpointId: "", dueTime: "" });
      setShowManualAdd(false);
    } catch { toast.error("Failed to add schedule"); }
  };

  const handleEdit = (row: typeof enrichedSchedule[0]) => {
    setEditingId(row.id);
    const h = row.dueAt.getHours().toString().padStart(2, "0");
    const m = row.dueAt.getMinutes().toString().padStart(2, "0");
    setEditForm({ officerId: row.officerId, checkpointId: row.checkpointId, dueTime: `${h}:${m}` });
  };

  const handleSaveEdit = async (id: string) => {
    const dueAt = new Date(`${viewDate}T${editForm.dueTime}:00`);
    try {
      await updateSchedule.mutateAsync({
        id,
        updates: {
          officer_id: editForm.officerId || undefined,
          checkpoint_id: editForm.checkpointId || undefined,
          due_at: dueAt.toISOString(),
        },
      });
      toast.success("Schedule updated");
      setEditingId(null);
    } catch { toast.error("Failed to update"); }
  };

  const toggleOfficerSelection = (id: string) => {
    setGenForm(prev => ({
      ...prev,
      selectedOfficers: prev.selectedOfficers.includes(id)
        ? prev.selectedOfficers.filter(x => x !== id)
        : [...prev.selectedOfficers, id],
    }));
  };

  const toggleCheckpointSelection = (id: string) => {
    setGenForm(prev => ({
      ...prev,
      selectedCheckpoints: prev.selectedCheckpoints.includes(id)
        ? prev.selectedCheckpoints.filter(x => x !== id)
        : [...prev.selectedCheckpoints, id],
    }));
  };

  const statusColors = {
    completed: { bg: "bg-[hsl(var(--app-green)/0.15)]", text: "text-[hsl(var(--app-green))]", label: "✓ DONE" },
    overdue: { bg: "bg-[hsl(var(--app-red)/0.15)]", text: "text-[hsl(var(--app-red))]", label: "OVERDUE" },
    due_soon: { bg: "bg-[hsl(var(--app-blue)/0.15)]", text: "text-[hsl(var(--app-blue))]", label: "DUE SOON" },
    pending: { bg: "bg-[hsl(var(--app-border-subtle))]", text: "text-[hsl(var(--app-text-muted))]", label: "PENDING" },
  };

  return (
    <div className="space-y-4">
      {/* Header Controls */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">PATROL SCHEDULE</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => exportSchedulePdf(enrichedSchedule.map(s => ({ due: s.dueTime, checkpoint: s.checkpointName, site: s.siteName, officer: s.officerName, status: s.status, actual: s.actualTime || "—", variance: s.varianceMinutes != null ? `${s.varianceMinutes}m` : "—" })))}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export PDF
            </button>
            <input
              type="date"
              value={viewDate}
              onChange={e => setViewDate(e.target.value)}
              className="rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-1.5 text-xs"
            />
            <button onClick={() => setShowManualAdd(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Entry
            </button>
            <button onClick={() => setShowGenerator(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-semibold hover:opacity-90 transition-opacity">
              <RotateCcw className="w-3.5 h-3.5" /> Generate Rotation
            </button>
          </div>
        </div>

        {/* Site selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-xs text-[hsl(var(--app-text-muted))] shrink-0">Site:</span>
          {sites.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedSite(s.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                selectedSite === s.id
                  ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]"
                  : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold)/0.5)]"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {!selectedSite && (
        <div className="app-card rounded-xl p-8 text-center">
          <MapPin className="w-10 h-10 text-[hsl(var(--app-text-muted))] mx-auto mb-3 opacity-50" />
          <p className="text-sm text-[hsl(var(--app-text-muted))]">Select a site above to view and manage its patrol schedule</p>
        </div>
      )}

      {selectedSite && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { value: `${stats.coverage}%`, label: "COVERAGE", color: "app-gold" },
              { value: String(stats.completed), label: "COMPLETED", color: "app-green" },
              { value: String(stats.overdue), label: "OVERDUE", color: "app-red" },
              { value: String(stats.pending), label: "PENDING", color: "app-blue" },
            ].map(s => (
              <div key={s.label} className="app-card rounded-xl p-3 text-center">
                <p className={`font-bebas text-2xl text-[hsl(var(--${s.color}))]`}>{s.value}</p>
                <p className="text-[9px] font-semibold tracking-wider text-[hsl(var(--app-text-muted))] uppercase">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Overdue Alerts */}
          {enrichedSchedule.filter(s => s.status === "overdue").length > 0 && (
            <div className="space-y-2">
              <h3 className="font-bebas text-base text-[hsl(var(--app-red))] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> OVERDUE CHECK-INS
              </h3>
              {enrichedSchedule.filter(s => s.status === "overdue").map(s => (
                <div key={s.id} className="app-card rounded-xl p-3 border-l-4 border-l-[hsl(var(--app-red))]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">
                        <User className="w-3.5 h-3.5 inline mr-1" />{s.officerName} → <MapPin className="w-3.5 h-3.5 inline mr-1" />{s.checkpointName}
                      </p>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">Due at {s.dueTime}</p>
                    </div>
                    <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[hsl(var(--app-red)/0.15)] text-[hsl(var(--app-red))] animate-pulse">
                      +{s.overdueMinutes}m LATE
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Status Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs text-[hsl(var(--app-text-muted))] shrink-0">Filter:</span>
            {([
              { key: "all", label: "All", count: stats.total },
              { key: "overdue", label: "Overdue", count: stats.overdue },
              { key: "completed", label: "Completed", count: stats.completed },
              { key: "pending", label: "Pending", count: stats.pending },
            ] as const).map(f => (
              <button
                key={f.key}
                onClick={() => setScheduleFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                  scheduleFilter === f.key
                    ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]"
                    : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold)/0.5)]"
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>

          {/* Schedule Table */}
          <div className="app-card rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--app-border-subtle))]">
                  {["Time", "Officer", "Checkpoint", "Status", "Actual", "Variance", "GPS", "Method", ""].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enrichedSchedule.filter(s => scheduleFilter === "all" ? true : s.status === scheduleFilter || (scheduleFilter === "pending" && s.status === "due_soon")).map((row, i) => {
                  const sc = statusColors[row.status];
                  const isEditing = editingId === row.id;

                  return (
                    <tr key={row.id} className={`border-b border-[hsl(var(--app-border-subtle))] ${i % 2 === 0 ? "bg-[hsl(var(--app-surface-2)/0.3)]" : ""} ${row.status === "overdue" ? "bg-[hsl(var(--app-red)/0.05)]" : ""}`}>
                      <td className="px-3 py-2.5">
                        {isEditing ? (
                          <input type="time" value={editForm.dueTime} onChange={e => setEditForm(f => ({ ...f, dueTime: e.target.value }))}
                            className="w-24 rounded border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-1 py-0.5 text-xs" />
                        ) : (
                          <span className="font-mono-data text-[hsl(var(--app-gold))] text-xs font-semibold">{row.dueTime}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {isEditing ? (
                          <select value={editForm.officerId} onChange={e => setEditForm(f => ({ ...f, officerId: e.target.value }))}
                            className="w-full rounded border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-1 py-0.5 text-xs">
                            {siteOfficers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        ) : (
                          <span className="text-[hsl(var(--app-text-primary))] text-xs font-medium flex items-center gap-1">
                            <User className="w-3 h-3 text-[hsl(var(--app-text-muted))]" /> {row.officerName}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {isEditing ? (
                          <select value={editForm.checkpointId} onChange={e => setEditForm(f => ({ ...f, checkpointId: e.target.value }))}
                            className="w-full rounded border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-1 py-0.5 text-xs">
                            {siteCheckpoints.map(cp => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
                          </select>
                        ) : (
                          <span className="text-[hsl(var(--app-text-secondary))] text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {row.checkpointName}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sc.bg} ${sc.text}`}>
                          {row.status === "overdue" ? `${sc.label} +${row.overdueMinutes}m` : sc.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-mono-data text-xs text-[hsl(var(--app-text-secondary))]">
                        {row.actualTime || "—"}
                      </td>
                      <td className="px-3 py-2.5 font-mono-data text-xs">
                        {row.varianceMinutes != null ? (
                          <span className={row.varianceMinutes <= 2 ? "text-[hsl(var(--app-green))]" : row.varianceMinutes <= 5 ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-red))]"}>
                            {row.varianceMinutes >= 0 ? `+${row.varianceMinutes}m` : `${row.varianceMinutes}m`}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-[10px] font-mono-data text-[hsl(var(--app-text-muted))]">
                        {row.gpsCoords && typeof row.gpsCoords === "object" && "lat" in (row.gpsCoords as any)
                          ? `${((row.gpsCoords as any).lat as number).toFixed(4)}, ${((row.gpsCoords as any).lng as number).toFixed(4)}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {row.method ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--app-purple)/0.15)] text-[hsl(var(--app-purple))]">{row.method}</span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              <button onClick={() => handleSaveEdit(row.id)} className="text-[hsl(var(--app-green))] hover:opacity-70"><Save className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditingId(null)} className="text-[hsl(var(--app-text-muted))] hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
                            </>
                          ) : (
                            <>
                              {row.status !== "completed" && (
                                <button onClick={() => handleEdit(row)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-gold))] transition-colors"><Edit3 className="w-3.5 h-3.5" /></button>
                              )}
                              <button onClick={async () => { try { await removeSchedule.mutateAsync(row.id); toast.success("Removed"); } catch { toast.error("Failed"); } }}
                                className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-red))] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {enrichedSchedule.length === 0 && (
              <div className="text-center py-10">
                <Clock className="w-8 h-8 text-[hsl(var(--app-text-muted))] mx-auto mb-3 opacity-40" />
                <p className="text-sm text-[hsl(var(--app-text-muted))]">No schedules for this date.</p>
                <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">Use "Generate Rotation" to auto-create hourly patrol schedules.</p>
              </div>
            )}
          </div>

          {/* Timeline View by Officer */}
          {enrichedSchedule.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">OFFICER TIMELINE</h3>
              {Array.from(new Set(enrichedSchedule.map(s => s.officerId))).map(offId => {
                const officer = officers.find(o => o.id === offId);
                const officerSchedule = enrichedSchedule.filter(s => s.officerId === offId);
                const completedCount = officerSchedule.filter(s => s.status === "completed").length;

                return (
                  <div key={offId} className="app-card rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${officer?.status === "live" ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-text-muted))]"}`} />
                        <span className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{officer?.name || "Unknown"}</span>
                      </div>
                      <span className="text-xs text-[hsl(var(--app-text-muted))]">{completedCount}/{officerSchedule.length} done</span>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {officerSchedule.map(s => {
                        const sc = statusColors[s.status];
                        return (
                          <div key={s.id} className={`shrink-0 rounded-lg px-2.5 py-1.5 ${sc.bg} border border-transparent`}>
                            <p className={`text-[10px] font-bold ${sc.text}`}>{s.dueTime}</p>
                            <p className="text-[9px] text-[hsl(var(--app-text-muted))] truncate max-w-[80px]">{s.checkpointName}</p>
                            {s.actualTime && <p className="text-[9px] text-[hsl(var(--app-green))]">✓ {s.actualTime}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Generate Rotation Modal */}
      {showGenerator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="app-card rounded-2xl p-6 w-full max-w-lg mx-4 space-y-4 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowGenerator(false)} className="absolute top-4 right-4 text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"><X className="w-5 h-5" /></button>
            <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[hsl(var(--app-gold))]" /> GENERATE ROTATION SCHEDULE
            </h3>
            <p className="text-xs text-[hsl(var(--app-text-muted))]">
              Automatically create hourly patrol assignments. Officers rotate through checkpoints each hour to ensure full coverage.
            </p>

            <div className="space-y-4">
              {/* Site selector */}
              <div>
                <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Select Site *</label>
                <select value={genForm.siteId} onChange={e => setGenForm(f => ({ ...f, siteId: e.target.value, selectedOfficers: [], selectedCheckpoints: [] }))}
                  className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                  <option value="">— Choose a site —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {genForm.siteId && (
                <>
                  {/* Officers */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-[hsl(var(--app-text-muted))]">Select Officers</label>
                      <button onClick={() => setGenForm(f => ({ ...f, selectedOfficers: f.selectedOfficers.length === genSiteOfficers.length ? [] : genSiteOfficers.map(o => o.id) }))}
                        className="text-[10px] text-[hsl(var(--app-gold))] hover:underline">
                        {genForm.selectedOfficers.length === genSiteOfficers.length ? "Deselect All" : "Select All"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-[120px] overflow-y-auto">
                      {genSiteOfficers.map(o => (
                        <button key={o.id} onClick={() => toggleOfficerSelection(o.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
                            genForm.selectedOfficers.includes(o.id)
                              ? "bg-[hsl(var(--app-gold)/0.15)] border border-[hsl(var(--app-gold))] text-[hsl(var(--app-gold))]"
                              : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold)/0.3)]"
                          }`}>
                          <CheckCircle2 className={`w-3.5 h-3.5 ${genForm.selectedOfficers.includes(o.id) ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-border-subtle))]"}`} />
                          {o.name}
                        </button>
                      ))}
                    </div>
                    {genSiteOfficers.length === 0 && <p className="text-xs text-[hsl(var(--app-text-muted))] italic">No officers assigned to this site</p>}
                  </div>

                  {/* Checkpoints */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-[hsl(var(--app-text-muted))]">Select Checkpoints</label>
                      <button onClick={() => setGenForm(f => ({ ...f, selectedCheckpoints: f.selectedCheckpoints.length === genSiteCheckpoints.length ? [] : genSiteCheckpoints.map(cp => cp.id) }))}
                        className="text-[10px] text-[hsl(var(--app-gold))] hover:underline">
                        {genForm.selectedCheckpoints.length === genSiteCheckpoints.length ? "Deselect All" : "Select All"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-[120px] overflow-y-auto">
                      {genSiteCheckpoints.map(cp => (
                        <button key={cp.id} onClick={() => toggleCheckpointSelection(cp.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
                            genForm.selectedCheckpoints.includes(cp.id)
                              ? "bg-[hsl(var(--app-gold)/0.15)] border border-[hsl(var(--app-gold))] text-[hsl(var(--app-gold))]"
                              : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold)/0.3)]"
                          }`}>
                          <MapPin className={`w-3.5 h-3.5 ${genForm.selectedCheckpoints.includes(cp.id) ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-border-subtle))]"}`} />
                          {cp.name}
                        </button>
                      ))}
                    </div>
                    {genSiteCheckpoints.length === 0 && <p className="text-xs text-[hsl(var(--app-text-muted))] italic">No checkpoints created for this site yet</p>}
                  </div>

                  {/* Shift times */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Shift Start</label>
                      <input type="time" value={genForm.shiftStart} onChange={e => setGenForm(f => ({ ...f, shiftStart: e.target.value }))}
                        className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Shift End</label>
                      <input type="time" value={genForm.shiftEnd} onChange={e => setGenForm(f => ({ ...f, shiftEnd: e.target.value }))}
                        className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Interval</label>
                      <select value={genForm.interval} onChange={e => setGenForm(f => ({ ...f, interval: Number(e.target.value) }))}
                        className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                        <option value={30}>30 min</option>
                        <option value={60}>1 hour</option>
                        <option value={90}>1.5 hours</option>
                        <option value={120}>2 hours</option>
                      </select>
                    </div>
                  </div>

                  {/* Preview */}
                  {genForm.selectedOfficers.length > 0 && genForm.selectedCheckpoints.length > 0 && (
                    <div className="rounded-lg bg-[hsl(var(--app-surface-2))] p-3">
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">
                        This will create <span className="font-bold text-[hsl(var(--app-gold))]">
                          {(() => {
                            const [sH, sM] = genForm.shiftStart.split(":").map(Number);
                            const [eH, eM] = genForm.shiftEnd.split(":").map(Number);
                            let st = sH * 60 + sM, et = eH * 60 + eM;
                            if (et <= st) et += 1440;
                            const slots = Math.floor((et - st) / genForm.interval);
                            return slots * genForm.selectedOfficers.length;
                          })()}
                        </span> schedule entries across <span className="font-bold">{genForm.selectedOfficers.length}</span> officers and <span className="font-bold">{genForm.selectedCheckpoints.length}</span> checkpoints at <span className="font-bold">{sites.find(s => s.id === genForm.siteId)?.name}</span>.
                        Officers will rotate checkpoints every {genForm.interval} minutes.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowGenerator(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] text-sm hover:bg-[hsl(var(--app-surface-2))]">Cancel</button>
              <button onClick={handleGenerateRotation} disabled={generateRotation.isPending || !genForm.siteId}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {generateRotation.isPending ? "Generating..." : "Generate Schedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Add Modal */}
      {showManualAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="app-card rounded-2xl p-6 w-full max-w-md mx-4 space-y-4 relative">
            <button onClick={() => setShowManualAdd(false)} className="absolute top-4 right-4 text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"><X className="w-5 h-5" /></button>
            <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">ADD SCHEDULE ENTRY</h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Site *</label>
                <select value={manualForm.siteId} onChange={e => setManualForm(f => ({ ...f, siteId: e.target.value, officerId: "", checkpointId: "" }))}
                  className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                  <option value="">— Choose a site —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {manualForm.siteId && (
                <>
                  <div>
                    <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Officer *</label>
                    <select value={manualForm.officerId} onChange={e => setManualForm(f => ({ ...f, officerId: e.target.value }))}
                      className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                      <option value="">Select officer</option>
                      {manualSiteOfficers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Checkpoint</label>
                    <select value={manualForm.checkpointId} onChange={e => setManualForm(f => ({ ...f, checkpointId: e.target.value }))}
                      className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                      <option value="">Select checkpoint</option>
                      {manualSiteCheckpoints.map(cp => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Time *</label>
                    <input type="time" value={manualForm.dueTime} onChange={e => setManualForm(f => ({ ...f, dueTime: e.target.value }))}
                      className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm" />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowManualAdd(false)} className="flex-1 px-4 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] text-sm">Cancel</button>
              <button onClick={handleManualAdd} disabled={addSchedule.isPending || !manualForm.siteId}
                className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {addSchedule.isPending ? "Adding..." : "Add Entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
